import { useMutation } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import {
  formatStatementAmount,
  formatStatementDate,
  formatStatementPeriod,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatements.format'
import type {
  StatementDocumentFormat,
  StatementSummary,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsDemoData'
import { buildStatementDemoDocument } from 'src/page-components/Accounts/AccountStatementsTab/statementDemoDocument'
import { deliverStatementDocument } from 'src/page-components/Accounts/AccountStatementsTab/statementDocumentDelivery'
import { useStatementDocumentFormatLabels } from 'src/page-components/Accounts/AccountStatementsTab/statementDocumentFormatLabels'
import { useStatementStatusLabel } from 'src/page-components/Accounts/AccountStatementsTab/useStatementLabels'
import type { QueryError } from 'src/queries/network.utils'
import type { CardManagementStatementDocument } from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import { CARD_MANAGEMENT_STATEMENT_DOCUMENT_MEDIA } from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import { physicalCardManagementMutations } from 'src/queries/physicalCardManagement/physicalCardManagement.queries'

/**
 * Downloading one statement's document (EX-40602).
 *
 * Owns the whole act: fetch (or, for a demo statement, generate), hand to the
 * browser, and translate any failure into a sentence the row can show. A row
 * renders the result; it does not know which of the two sources answered.
 *
 * The failure copy is the point of this hook. The contract distinguishes three
 * document errors that mean genuinely different things to the person who just
 * clicked Download — this format does not exist for this statement, the
 * statement itself is gone, and the document service is briefly down — and only
 * the last is worth retrying. Collapsing them into one "something went wrong"
 * would send people back to a control that will never work.
 */

/** Contract error codes this surface can explain (`CardManagementServiceError`). */
const STATEMENT_NOT_FOUND = 'CARD-MGMT.25'
const DOCUMENT_FORMAT_UNAVAILABLE = 'CARD-MGMT.26'
const DOCUMENT_UNAVAILABLE = 'CARD-MGMT.27'

export interface StatementDownloadResult {
  /** Starts a download. No-op while one is already in flight. */
  download: (format: StatementDocumentFormat) => void
  /** True from the moment a format is picked until the file is delivered. */
  isDownloading: boolean
  /** A translated sentence, or `null`. Cleared when a new download starts. */
  errorMessage: string | null
}

/**
 * @param creditCardId - The card the statement belongs to.
 * @param statement - The statement to download.
 * @param cardLabel - Card label written into a generated demo document.
 * @param isDemoStatement - True when `statement` is the fabricated fallback, in
 *   which case no request is made: its id does not exist server-side, so the
 *   real route would only answer `404 CARD-MGMT.25`.
 */
export const useStatementDownload = (
  creditCardId: string | undefined,
  statement: StatementSummary,
  cardLabel: string,
  isDemoStatement: boolean,
): StatementDownloadResult => {
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()
  const statusLabel = useStatementStatusLabel(statement.status)
  const formatLabels = useStatementDocumentFormatLabels()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const downloadMutation = useMutation(
    physicalCardManagementMutations.downloadStatementDocument(),
  )
  const { mutate } = downloadMutation

  const describeFailure = useCallback(
    (error: QueryError, format: StatementDocumentFormat): string => {
      // The LABEL the user clicked, never the wire enum: "isn't available as
      // XLSX" names a token that appears nowhere in the menu they just used.
      const formatName = formatLabels[format]

      switch (error.errorCode ?? error.code) {
        case DOCUMENT_FORMAT_UNAVAILABLE:
          return translate('This statement isn’t available as ${format}.', {
            format: formatName,
          })
        case DOCUMENT_UNAVAILABLE:
          return translate(
            'Statement downloads are temporarily unavailable. Try again in a few minutes.',
          )
        case STATEMENT_NOT_FOUND:
          return translate('We couldn’t find this statement.')
        default:
          break
      }

      /*
       * Falling back to the HTTP STATUS before the generic sentence.
       *
       * The contract codes above only arrive once the document endpoint is
       * actually serving this card. Until then — and whenever a proxy, gateway,
       * or auth layer answers instead of the service — the failure carries a
       * status and no code, and collapsing all of those into one "try again"
       * hides the difference between "this deployment has no document route
       * yet", "you are not permitted", and "the service is down". Every branch
       * below is a real answer someone can act on.
       */
      switch (error.status) {
        case 401:
        case 403:
          return translate('You don’t have access to this statement file.')
        case 404:
          return translate(
            'Statement downloads aren’t available for this card yet.',
          )
        case 422:
          return translate('This statement isn’t available as ${format}.', {
            format: formatName,
          })
        case 503:
          return translate(
            'Statement downloads are temporarily unavailable. Try again in a few minutes.',
          )
        default:
          return translate('We couldn’t download this statement. Try again.')
      }
    },
    [translate, formatLabels],
  )

  /**
   * The name the file is saved under.
   *
   * Derived from the statement PERIOD, not its id: the server's own name almost
   * never reaches the browser (`Content-Disposition` is not CORS-exposed, see
   * `CardManagementStatementDocument.fileName`), and the id-based fallback that
   * stood in for it produced `statement-pcmstmt_AAAAAAAAAAAAAAAAAAAAAA.pdf` in
   * the user's downloads folder. `statement-2026-06.pdf` is the same name the
   * demo path already writes, so both sources agree.
   */
  const resolveFileName = useCallback(
    (statementDocument: CardManagementStatementDocument): string =>
      statementDocument.fileName ??
      `statement-${statement.period.slice(0, 7)}.${
        CARD_MANAGEMENT_STATEMENT_DOCUMENT_MEDIA[statementDocument.format]
          .fileExtension
      }`,
    [statement.period],
  )

  const deliver = useCallback(
    (
      statementDocument: CardManagementStatementDocument,
      pdfTab: Window | null,
    ) => {
      deliverStatementDocument(
        {
          ...statementDocument,
          fileName: resolveFileName(statementDocument),
        },
        pdfTab,
      )
    },
    [resolveFileName],
  )

  const download = useCallback(
    (format: StatementDocumentFormat) => {
      if (downloadMutation.isPending) {
        return
      }

      setErrorMessage(null)

      /*
       * The PDF tab is opened HERE — synchronously, inside the click — and
       * navigated later, once the bytes exist.
       *
       * Opening it at delivery time works on the demo path (that build is
       * synchronous) and fails on the real one: delivery runs from the
       * mutation's `onSuccess`, after an awaited round trip, by which point the
       * click task is over. Chrome's transient activation usually covers a fast
       * request, but Safari only permits `window.open` synchronously within the
       * gesture and Firefox blocks it by default — so the PRIMARY format would
       * have been reported blocked on every attempt for those users.
       *
       * Blank rather than pre-navigated because the object url does not exist
       * yet. Both sources open it the same way, so there is one behaviour to
       * reason about rather than a demo path that happens to work.
       */
      const pdfTab = format === 'PDF' ? window.open('', '_blank') : null

      if (isDemoStatement) {
        // Written in the browser rather than requested: see
        // `statementDemoDocument`.
        try {
          deliver(
            buildStatementDemoDocument({
              statement,
              transactions: statement.transactions ?? [],
              format,
              cardLabel,
              periodLabel: formatStatementPeriod(statement.period),
              statusLabel,
              dueLabel: formatStatementDate(statement.dueOn),
              formatAmount: (money) =>
                formatStatementAmount(formatCurrency, money),
              formatDate: formatStatementDate,
            }),
            pdfTab,
          )
        } catch {
          pdfTab?.close()
          setErrorMessage(
            translate('We couldn’t download this statement. Try again.'),
          )
        }
        return
      }

      if (!creditCardId) {
        pdfTab?.close()
        setErrorMessage(
          translate('We couldn’t download this statement. Try again.'),
        )
        return
      }

      mutate(
        { creditCardId, statementId: statement.id, format },
        {
          onSuccess: (statementDocument) => deliver(statementDocument, pdfTab),
          onError: (error) => {
            // Closed, or the user is left staring at a blank tab that will
            // never receive a document.
            pdfTab?.close()
            setErrorMessage(describeFailure(error, format))
          },
        },
      )
    },
    [
      downloadMutation.isPending,
      isDemoStatement,
      statement,
      cardLabel,
      statusLabel,
      formatCurrency,
      creditCardId,
      mutate,
      deliver,
      describeFailure,
      translate,
    ],
  )

  return {
    download,
    isDownloading: downloadMutation.isPending,
    errorMessage,
  }
}
